import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ScriptLine, RecordedAudioEntry } from '../lib/scriptParser';

export type FlowSource = 'new' | 'saved';
export type VoiceSelection = 'female' | 'male';

export interface EditLine {
    label: string;
    dialogue: string;
    recordedAudioId?: string | null;
}

interface ScriptState {
    parsedLines: ScriptLine[];
    characters: string[];
    myCharacters: string[];
    editedScriptLines: ScriptLine[] | null;
    currentScriptName: string;
    currentScriptId: string | null;
    currentRecordedAudio: RecordedAudioEntry[] | null;
    flowSource: FlowSource;
    useHumanRecordings: boolean;
}

const initialState: ScriptState = {
    parsedLines: [],
    characters: [],
    myCharacters: [],
    editedScriptLines: null,
    currentScriptName: '',
    currentScriptId: null,
    currentRecordedAudio: null,
    flowSource: 'new',
    useHumanRecordings: true,
};

const scriptSlice = createSlice({
    name: 'script',
    initialState,
    reducers: {
        setParsedLines(state, action: PayloadAction<ScriptLine[]>) {
            state.parsedLines = action.payload;
        },
        setCharacters(state, action: PayloadAction<string[]>) {
            state.characters = action.payload;
        },
        setMyCharacters(state, action: PayloadAction<string[]>) {
            state.myCharacters = action.payload;
        },
        addMyCharacter(state, action: PayloadAction<string>) {
            if (!state.myCharacters.includes(action.payload)) {
                state.myCharacters.push(action.payload);
            }
        },
        removeMyCharacter(state, action: PayloadAction<string>) {
            state.myCharacters = state.myCharacters.filter(c => c !== action.payload);
        },
        setEditedScriptLines(state, action: PayloadAction<ScriptLine[] | null>) {
            state.editedScriptLines = action.payload;
        },
        setScriptMeta(state, action: PayloadAction<{ name: string; id: string | null }>) {
            state.currentScriptName = action.payload.name;
            state.currentScriptId = action.payload.id;
        },
        setRecordedAudio(state, action: PayloadAction<RecordedAudioEntry[] | null>) {
            state.currentRecordedAudio = action.payload;
        },
        setFlowSource(state, action: PayloadAction<FlowSource>) {
            state.flowSource = action.payload;
        },
        resetScript(state) {
            state.editedScriptLines = null;
            state.myCharacters = [];
            state.flowSource = 'new';
        },
    },
});

export const {
    setParsedLines, setCharacters, setMyCharacters,
    addMyCharacter, removeMyCharacter,
    setEditedScriptLines, setScriptMeta, setRecordedAudio,
    setFlowSource, resetScript,
} = scriptSlice.actions;

export default scriptSlice.reducer;
